/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import * as types from "../../types/primitives.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  ActionComponent,
  ActionComponent$inboundSchema,
  ActionComponent$Outbound,
  ActionComponent$outboundSchema,
} from "./actioncomponent.js";
import {
  Animatic,
  Animatic$inboundSchema,
  Animatic$Outbound,
  Animatic$outboundSchema,
} from "./animatic.js";
import {
  DefrostScheduleConfig,
  DefrostScheduleConfig$inboundSchema,
  DefrostScheduleConfig$Outbound,
  DefrostScheduleConfig$outboundSchema,
} from "./defrostscheduleconfig.js";
import {
  DisplayRef,
  DisplayRef$inboundSchema,
  DisplayRef$Outbound,
  DisplayRef$outboundSchema,
} from "./displayref.js";
import {
  RelatedDeviceSection,
  RelatedDeviceSection$inboundSchema,
  RelatedDeviceSection$Outbound,
  RelatedDeviceSection$outboundSchema,
} from "./relateddevicesection.js";
import {
  SequencerConfigState,
  SequencerConfigState$inboundSchema,
  SequencerConfigState$Outbound,
  SequencerConfigState$outboundSchema,
} from "./sequencerconfigstate.js";
import {
  SequencerEditorConfig,
  SequencerEditorConfig$inboundSchema,
  SequencerEditorConfig$Outbound,
  SequencerEditorConfig$outboundSchema,
} from "./sequencereditorconfig.js";

/**
 * The equipment details response
 */
export type EquipmentDetailsResponse = {
  /**
   * The actions to display on the summary
   */
  actionComponents?: Array<ActionComponent> | undefined;
  /**
   * Defines the configuration of an animatic. The Animatic is made up of a primitive component, and optional labels, gauges, and a shelf.
   */
  animatic: Animatic;
  /**
   * Configuration needed for time of day defrost scheduler
   */
  defrostScheduleConfig?: DefrostScheduleConfig | undefined;
  /**
   * The metrics to display, this displays metrics in the Metrics section of the Equipment Details Screen. The Installer can add the same metrics in both this section and the featured section, but it doesn't make much sense.
   */
  displayMetrics: Array<DisplayRef>;
  /**
   * The settings to display, this displays settings in the Settings section of the Equipment Details Screen.
   */
  displaySettings: Array<DisplayRef>;
  /**
   * The featured metrics to display, this displays metrics in the Top features section of the Equipment Details Screen.
   */
  featuredMetrics?: Array<DisplayRef> | undefined;
  /**
   * The related devices to display, this displays related devices in the Equipment Details Screen.
   */
  relatedDevices?: Array<RelatedDeviceSection> | undefined;
  /**
   * The sequencer editor configuration, this gives information that the UI needs to build/display the sequencer editor.
   */
  sequencerEditorConfig?: SequencerEditorConfig | undefined;
  /**
   * The sequencers to display, this displays sequencers in the Sequencer section of the Equipment Details Screen.
   */
  sequencerStates?: Array<SequencerConfigState> | undefined;
  /**
   * The title of the equipment details screen
   */
  title?: string | undefined;
};

/** @internal */
export const EquipmentDetailsResponse$inboundSchema: z.ZodType<
  EquipmentDetailsResponse,
  z.ZodTypeDef,
  unknown
> = z.object({
  action_components: types.optional(z.array(ActionComponent$inboundSchema)),
  animatic: Animatic$inboundSchema,
  defrost_schedule_config: types.optional(DefrostScheduleConfig$inboundSchema),
  display_metrics: z.array(DisplayRef$inboundSchema),
  display_settings: z.array(DisplayRef$inboundSchema),
  featured_metrics: types.optional(z.array(DisplayRef$inboundSchema)),
  related_devices: types.optional(z.array(RelatedDeviceSection$inboundSchema)),
  sequencer_editor_config: types.optional(SequencerEditorConfig$inboundSchema),
  sequencer_states: types.optional(z.array(SequencerConfigState$inboundSchema)),
  title: types.optional(types.string()),
}).transform((v) => {
  return remap$(v, {
    "action_components": "actionComponents",
    "defrost_schedule_config": "defrostScheduleConfig",
    "display_metrics": "displayMetrics",
    "display_settings": "displaySettings",
    "featured_metrics": "featuredMetrics",
    "related_devices": "relatedDevices",
    "sequencer_editor_config": "sequencerEditorConfig",
    "sequencer_states": "sequencerStates",
  });
});
/** @internal */
export type EquipmentDetailsResponse$Outbound = {
  action_components?: Array<ActionComponent$Outbound> | undefined;
  animatic: Animatic$Outbound;
  defrost_schedule_config?: DefrostScheduleConfig$Outbound | undefined;
  display_metrics: Array<DisplayRef$Outbound>;
  display_settings: Array<DisplayRef$Outbound>;
  featured_metrics?: Array<DisplayRef$Outbound> | undefined;
  related_devices?: Array<RelatedDeviceSection$Outbound> | undefined;
  sequencer_editor_config?: SequencerEditorConfig$Outbound | undefined;
  sequencer_states?: Array<SequencerConfigState$Outbound> | undefined;
  title?: string | undefined;
};

/** @internal */
export const EquipmentDetailsResponse$outboundSchema: z.ZodType<
  EquipmentDetailsResponse$Outbound,
  z.ZodTypeDef,
  EquipmentDetailsResponse
> = z.object({
  actionComponents: z.array(ActionComponent$outboundSchema).optional(),
  animatic: Animatic$outboundSchema,
  defrostScheduleConfig: DefrostScheduleConfig$outboundSchema.optional(),
  displayMetrics: z.array(DisplayRef$outboundSchema),
  displaySettings: z.array(DisplayRef$outboundSchema),
  featuredMetrics: z.array(DisplayRef$outboundSchema).optional(),
  relatedDevices: z.array(RelatedDeviceSection$outboundSchema).optional(),
  sequencerEditorConfig: SequencerEditorConfig$outboundSchema.optional(),
  sequencerStates: z.array(SequencerConfigState$outboundSchema).optional(),
  title: z.string().optional(),
}).transform((v) => {
  return remap$(v, {
    actionComponents: "action_components",
    defrostScheduleConfig: "defrost_schedule_config",
    displayMetrics: "display_metrics",
    displaySettings: "display_settings",
    featuredMetrics: "featured_metrics",
    relatedDevices: "related_devices",
    sequencerEditorConfig: "sequencer_editor_config",
    sequencerStates: "sequencer_states",
  });
});

export function equipmentDetailsResponseToJSON(
  equipmentDetailsResponse: EquipmentDetailsResponse,
): string {
  return JSON.stringify(
    EquipmentDetailsResponse$outboundSchema.parse(equipmentDetailsResponse),
  );
}
export function equipmentDetailsResponseFromJSON(
  jsonString: string,
): SafeParseResult<EquipmentDetailsResponse, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => EquipmentDetailsResponse$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'EquipmentDetailsResponse' from JSON`,
  );
}
